"use client";

import { RefObject, useCallback, useEffect, useState } from "react";

/** Share of the row's visible width one arrow press scrolls. */
const PAGE_FRACTION = 0.8;

/** Whether a horizontal scroll row can scroll further toward its start or its end, and a way to page it. */
export default function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState({ canScrollStart: false, canScrollEnd: false });

  const update = useCallback(() => {
    const row = ref.current;
    if (!row) {
      return;
    }
    // Sub-pixel scroll positions never quite reach 0 or the end.
    const canScrollStart = row.scrollLeft > 1;
    const canScrollEnd = row.scrollLeft < row.scrollWidth - row.clientWidth - 1;
    setEdges((previous) =>
      previous.canScrollStart === canScrollStart && previous.canScrollEnd === canScrollEnd
        ? previous
        : { canScrollStart, canScrollEnd },
    );
  }, [ref]);

  useEffect(() => {
    const row = ref.current;
    if (!row) {
      return;
    }
    row.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ref, update]);

  // A pill's width changes when it's selected, which resizes the content but not the row.
  useEffect(update);

  const page = (direction: "start" | "end") => {
    const row = ref.current;
    if (!row) {
      return;
    }
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollBy({
      left: (direction === "start" ? -1 : 1) * row.clientWidth * PAGE_FRACTION,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  return { ...edges, page };
}

interface ScrollEdges {
  canScrollStart: boolean;
  canScrollEnd: boolean;
  page: (direction: "start" | "end") => void;
}
